/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import debounce from 'lodash.debounce';
import * as React from 'react';
import { getLifetime } from 'src/core/utils/lifetime';
import { watchResize } from 'src/core/utils/resizeWatcher';

export const useSplitPane = (
    options: {
        defaultSize: number | string;
        maxSize: number | string;
    },
    delay: number = 50
) => {
    const [size, setSize] = React.useState<number | string>(options.defaultSize ?? 0);
    const [maxSize, setMaxSize] = React.useState<number | string>(options.maxSize ?? 0);

    // Keeps the size in sync.
    React.useEffect(() => {
        setSize(options.defaultSize ?? 0);
    }, [options.defaultSize]);

    // Keeps the max size in sync.
    React.useEffect(() => {
        setMaxSize(options.maxSize ?? 0);
    }, [options.maxSize]);

    // Debounced setter for size
    const debouncedSetSize = debounce(setSize, delay);

    return { size, setSize: debouncedSetSize, maxSize, setMaxSize };
};

export const useVerticalSplitPane = (element: Element, mainMinWidth: number, sideMinWidth: number) => {
    const { size, setSize, maxSize, setMaxSize } = useSplitPane({
        defaultSize: sideMinWidth,
        maxSize: 0
    });

    const recalculateSizeAllowance = (totalWidth: number) => {
        // Max allowed width for the side pane
        const newMaxWidth = totalWidth - mainMinWidth;
        // If current side pane width is bigger than the new max allowed width, update the width
        const newWidth = Math.min(<number>size, newMaxWidth);
        setSize(newWidth);

        // If max allowed width is less than min side pane width, update max allowed side pane with to min side pane width
        if (newMaxWidth < sideMinWidth) {
            setMaxSize(sideMinWidth);
        } else if (maxSize !== newMaxWidth) {
            // Update max allowed width if it's a new value
            setMaxSize(newMaxWidth);
        }
    };

    React.useEffect(() => {
        const lifetime = getLifetime();

        if (element) {
            const watcher = watchResize(element, dim => {
                // On resize re-calculate max width side pane allowance
                recalculateSizeAllowance(dim.width);
            });
            lifetime.add(watcher.unobserve);
        }

        return lifetime.dispose;
    });

    return { size, setSize, maxSize, setMaxSize };
};
